import { Component, computed, input, output, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatIconModule } from '@angular/material/icon'
import { fromLabel } from '@seiri/shared/common'

let nextId = 0

/**
 * The head of a list of days: "Od pondelka 5. októbra" and, in the same row, "Vybrať dátum", which
 * opens an inline calendar under it. Picking a day closes the calendar and emits it; the host
 * moves its list. The host provides the `DateAdapter`.
 *
 * Dates in and out are the calendar's: the day's browser-local midnight, standing for that day on
 * the premise's wall clock.
 */
@Component({
  selector: 'seiri-day-list-header',
  imports: [MatButtonModule, MatDatepickerModule, MatIconModule],
  template: `
    <div class="header">
      <p class="header__from">{{ fromText() }}</p>
      <button
        type="button"
        mat-stroked-button
        color="primary"
        class="small"
        [attr.aria-controls]="calendarId"
        [attr.aria-expanded]="open()"
        (click)="open.set(!open())"
      >
        <mat-icon>calendar_month</mat-icon>
        {{ open() ? 'Skryť kalendár' : 'Vybrať dátum' }}
      </button>
    </div>
    @if (open()) {
      <mat-calendar
        class="calendar"
        [id]="calendarId"
        [minDate]="min()"
        [maxDate]="max()"
        [selected]="selected()"
        [startAt]="selected()"
        (selectedChange)="pick($event)"
      />
    }
  `,
  styles: `
    :host {
      display: block;
    }
    .header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-8) var(--space-16);
    }
    .header__from {
      margin: 0;
      font: var(--mat-sys-title-small);
      color: var(--theme-fg-heading);
    }
    .calendar {
      max-width: 360px;
      margin-top: var(--space-12);
    }
  `,
})
export class DayListHeaderComponent {
  /** The day the list starts at, as `yyyy-MM-dd`: "Od pondelka 5. októbra". */
  readonly from = input.required<string>()
  readonly min = input<Date | null>(null)
  readonly max = input<Date | null>(null)
  /** Also where the calendar opens, so a day picked months ahead is not paged to again. */
  readonly selected = input<Date | null>(null)
  readonly picked = output<Date>()

  protected readonly calendarId = `day-list-calendar-${nextId++}`
  protected readonly open = signal(false)
  protected readonly fromText = computed(() => fromLabel(this.from()))

  protected pick(date: Date | null) {
    if (!date) return
    this.open.set(false)
    this.picked.emit(date)
  }
}
